/**
 * I FILTRI DELLA «LIVE» DELLA HUB CR, senza React (un banco la esegue sotto Node).
 *
 * Decisione di Luca nella call del 05/10 (r11): la Live ha TRE filtri — tornei, sit&go, code ranked — e
 * niente amichevoli (le sfide fra amici stanno fuori da tutto: `FAMIGLIE_LIVE`, `partiteInCorso.ts`).
 *
 * La forma la fissa il seggio (tma-orcb, dispatch 46) e si legge qui:
 *  · scelta MULTIPLA, tutti accesi all'apertura: la Live parte mostrando tutto, un filtro serve a togliere;
 *  · almeno uno resta acceso: l'ultimo acceso non si spegne;
 *  · un vuoto onesto per filtro: se le famiglie accese non hanno partite e le altre si', e' un vuoto
 *    DEL FILTRO, non «non c'e' niente da guardare» (`vuotoPerFiltri`);
 *  · ⛔ i filtri valgono per le partite degli ALTRI: la MIA partita in testa resta sempre, qualunque
 *    filtro sia acceso. I filtri servono a scorrere, la mia e' un'azione («Vai») e nasconderla con un
 *    filtro di lettura sorprende (tma-orcb, 06/10). L'unica che non c'e' mai e' la mia SFIDA, ma per la
 *    regola «niente sfide» di `partiteLive`, non per un filtro.
 * Una famiglia e' un filtro: il filtro non inventa una classificazione sua.
 *
 * ⚠️ Lo stato dei filtri e' del pannello (niente URL, niente storage): qui ci sono solo le regole.
 */
import type { FamigliaMostrata, Live } from './partiteInCorso.ts';

export type FiltroLive = FamigliaMostrata;

/** L'ordine dei filtri a schermo e' quello di Luca («tornei, sit and go e… le code ranked»). */
export const FILTRI_LIVE: readonly FiltroLive[] = Object.freeze(['torneo', 'sit-and-go', 'coda']);

/** I filtri accesi: tutti, all'apertura. */
export function tuttiAccesi(): ReadonlySet<FiltroLive> {
  return new Set(FILTRI_LIVE);
}

/**
 * Accende o spegne un filtro. L'ULTIMO acceso non si spegne: lo stesso insieme torna indietro (la stessa
 * istanza, cosi' un `setState` non ridisegna). Un valore che non e' un filtro non cambia niente.
 */
export function alternaFiltro(accesi: ReadonlySet<FiltroLive>, filtro: FiltroLive): ReadonlySet<FiltroLive> {
  if (!FILTRI_LIVE.includes(filtro)) return accesi;
  if (!accesi.has(filtro)) return new Set([...accesi, filtro]);
  if (accesi.size <= 1) return accesi;
  const resto = new Set(accesi);
  resto.delete(filtro);
  return resto;
}

/** Vero se questo e' l'unico filtro acceso: il bottone che lo spegnerebbe e' `disabled`. */
export const eUltimoAcceso = (accesi: ReadonlySet<FiltroLive>, filtro: FiltroLive): boolean =>
  accesi.size === 1 && accesi.has(filtro);

/**
 * La Live coi soli filtri accesi. Le sezioni delle famiglie spente escono (le altre restano, nell'ordine
 * di `FAMIGLIE_LIVE`), `totale` si ricalcola, e `mie` NON si tocca: v. l'intestazione.
 */
export function filtraLive(live: Live, accesi: ReadonlySet<FiltroLive>): Live {
  const sezioni = live.sezioni.filter((s) => accesi.has(s.famiglia));
  return { mie: live.mie, sezioni, totale: live.mie.length + sezioni.reduce((n, s) => n + s.totale, 0) };
}

/**
 * «Nessuna partita con questi filtri»: le famiglie accese non hanno partite degli altri e le altre si'.
 * ⚠️ Non e' il vuoto della Live: se NESSUNA famiglia ha partite (tutti i filtri accesi o no) non c'e'
 * niente da filtrare e il vuoto e' quello di sempre. E con una partita MIA a schermo il testo sta
 * comunque sotto di lei: dice che gli altri, con questi filtri, non ci sono.
 */
export const vuotoPerFiltri = (tutta: Live, filtrata: Live): boolean =>
  tutta.sezioni.length > 0 && filtrata.sezioni.length === 0;
